import { applyAppearanceOverrides } from "@commerce/shopper/overrides";
import type { AgentSettings, ProductCard } from "@repo/agent";
import type { AppearanceOverrides } from "@repo/embed";
import { getStorageImageUrl } from "@shared/lib/image-utils";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

export type ShopperAppearance = AgentSettings["appearance"];
export type ShopperVoice = AgentSettings["voice"];

/** Shown when the retailer has not written its own suggestions. */
const DEFAULT_STARTER_PROMPTS = [
	"What's on offer today?",
	"I need ingredients for dinner tonight",
];

export interface ShopperPageData {
	orgSlug: string;
	retailerName: string;
	logo: string | null;
	assistantName: string;
	starterPrompts: string[];
	/** The published appearance, with the app's overrides from the URL already applied. */
	appearance: ShopperAppearance;
	chat: AgentSettings["chat"];
	voice: ShopperVoice;
	support: AgentSettings["support"];
	/** Absolute URL of the logo: the page's icon on tabs and home screens. */
	logoUrl: string | null;
	/** Absolute URL of the 1200x630 link-preview card, when there is a logo. */
	shareCard: string | null;
	/** Absolute origin, for share links: the retailer's custom domain when served on one. */
	siteUrl: string;
	/** The store an anonymous visit opens in (the `store` hint, else the default). */
	storeId: string | null;
	/**
	 * Origins allowed to embed the page (settings plus live custom domains): the bridge only
	 * talks to one of these.
	 */
	allowedOrigins: string[];
	/** Chats open only from the retailer's app (launch token or code). */
	requireLaunchToken: boolean;
	/** The retailer's privacy notice, else ours. */
	privacyUrl: string;
	/**
	 * Hephon has not allowed this retailer to hide "Powered by Hephon": it shows whatever the
	 * settings or the app say (`chat.poweredBy` is already true then).
	 */
	poweredByLocked: boolean;
}

/**
 * The organization and published settings behind a shopper page, with the
 * suggestions resolved once: the page shows the same list the photos are for.
 * Server only: this module is also imported by the page, and the client build
 * must not pull the agent (and everything behind it) into a chunk.
 */
export const loadShopper = createServerOnlyFn(async (orgSlug: string) => {
	const [{ db }, { loadPublishedAgentConfig }] = await Promise.all([
		import("@repo/database"),
		import("@repo/agent"),
	]);
	const organization = await db.organization.findUnique({
		where: { slug: orgSlug },
		select: { id: true, name: true, logo: true },
	});
	if (!organization) {
		return null;
	}
	const { settings } = await loadPublishedAgentConfig(organization.id);
	const starterPrompts = settings.starterPrompts.length
		? settings.starterPrompts
		: DEFAULT_STARTER_PROMPTS;
	return { organization, settings, starterPrompts };
});

/**
 * Public data for the hosted chat page. Only what a shopper may see: no
 * config internals, no ids beyond the slug they already have in the URL.
 */
export const getShopperPageFn = createServerFn({ method: "GET" })
	.validator(
		(data: {
			orgSlug: string;
			store?: string | undefined;
			/** The app's appearance overrides, already validated by the route's search schema. */
			overrides?: AppearanceOverrides | undefined;
		}) => data,
	)
	.handler(async ({ data }): Promise<ShopperPageData | null> => {
		const [
			shopper,
			{ warmAgentConnections },
			{ getBaseUrl },
			{ createHash },
			{ getOrgSettings, allowedOriginsFor },
			{ findStoreOrDefault },
		] = await Promise.all([
			loadShopper(data.orgSlug),
			import("@repo/agent"),
			import("@repo/utils"),
			import("node:crypto"),
			import("@repo/database"),
			import("@repo/shopper-auth"),
		]);
		if (!shopper) {
			return null;
		}
		const [store, orgSettings] = await Promise.all([
			findStoreOrDefault(
				shopper.organization.id,
				data.store,
				shopper.settings.defaultStoreExternalId,
			),
			getOrgSettings(shopper.organization.id),
		]);
		const allowedOrigins = await allowedOriginsFor(
			shopper.organization.id,
			orgSettings,
		);
		// The shopper is about to type: have the agent's connections open.
		warmAgentConnections();
		const { organization, starterPrompts } = shopper;
		// Rendered on the server, so what the app changed is in the first paint.
		const settings = shopper.settings.appearance.allowAppOverrides
			? applyAppearanceOverrides(shopper.settings, data.overrides)
			: shopper.settings;
		// On the retailer's own domain, links and the share card stay on it. This app is
		// served only on its own host and live custom domains, so a request host that is
		// also an allowed origin is one of those domains.
		const requestOrigin = `https://${new URL(getRequest().url).host}`;
		const siteUrl = allowedOrigins.includes(requestOrigin)
			? requestOrigin
			: getBaseUrl().replace(/\/$/, "");
		const logo = getStorageImageUrl(organization.logo);
		// The card is cached by URL (WhatsApp and Facebook for days), so the
		// URL changes whenever the logo or colours behind it do.
		const cardVersion = createHash("sha1")
			.update(
				`${organization.logo}|${settings.appearance.primaryColor}|${settings.appearance.deepColor}`,
			)
			.digest("hex")
			.slice(0, 10);
		return {
			orgSlug: data.orgSlug,
			retailerName: organization.name,
			logo: organization.logo,
			assistantName: settings.persona.assistantName,
			starterPrompts,
			// An uploaded typeface replaces the preset (font-face.tsx declares it).
			appearance: settings.appearance.customFont
				? { ...settings.appearance, font: "geist" }
				: settings.appearance,
			chat: {
				...settings.chat,
				poweredBy:
					settings.chat.poweredBy || !orgSettings.allowHidePoweredBy,
			},
			voice: settings.voice,
			support: settings.support,
			logoUrl: logo?.startsWith("/") ? `${siteUrl}${logo}` : logo,
			shareCard: logo
				? `${siteUrl}/c/${data.orgSlug}/card.png?v=${cardVersion}`
				: null,
			siteUrl,
			storeId: store?.externalId ?? null,
			allowedOrigins,
			requireLaunchToken: orgSettings.requireLaunchToken,
			privacyUrl: settings.support.privacyUrl ?? "/legal/privacy",
			poweredByLocked: !orgSettings.allowHidePoweredBy,
		};
	});

/**
 * Opens the agent's connections again. The page calls it when a chat that was loaded ahead of
 * the tap, or kept after the sheet closed, comes on screen: the connections `getShopperPageFn`
 * opened live about a minute, and a first turn on cold ones is slow (the relevance check times
 * out). `warmAgentConnections` throttles itself, so this costs nothing when they are still open.
 */
export const warmAgentFn = createServerFn({ method: "GET" }).handler(
	async (): Promise<void> => {
		const { warmAgentConnections } = await import("@repo/agent");
		warmAgentConnections();
	},
);

/**
 * Catalog photos for the welcome screen's suggestions. Separate from the page
 * data so the first paint never waits on a search; tiles fill in when it lands.
 */
export const getStarterPhotosFn = createServerFn({ method: "GET" })
	.validator((data: { orgSlug: string; store: string | null }) => data)
	.handler(async ({ data }): Promise<Record<string, string[]>> => {
		const [shopper, { starterPhotos, turnExclusions }] = await Promise.all([
			loadShopper(data.orgSlug),
			import("@repo/agent"),
		]);
		if (!shopper) {
			return {};
		}
		return starterPhotos(
			shopper.organization.id,
			shopper.starterPrompts,
			data.store ?? shopper.settings.defaultStoreExternalId,
			turnExclusions(shopper.settings.restrictions),
		);
	});

/**
 * A few real products for the Configure screen's preview of a conversation: what the first
 * starter prompt finds. The same cards any visitor to the chat is shown.
 */
export const getPreviewProductsFn = createServerFn({ method: "GET" })
	.validator((data: { orgSlug: string }) => data)
	.handler(async ({ data }): Promise<ProductCard[]> => {
		const [shopper, { sampleProducts, turnExclusions }] = await Promise.all(
			[loadShopper(data.orgSlug), import("@repo/agent")],
		);
		const prompt = shopper?.starterPrompts[0];
		if (!shopper || !prompt) {
			return [];
		}
		return sampleProducts(
			shopper.organization.id,
			prompt,
			shopper.settings.defaultStoreExternalId,
			turnExclusions(shopper.settings.restrictions),
		);
	});
